import { useState, useCallback, useMemo, useEffect, useRef } from 'react';
import {
    Brain, FileText, Wrench, Sparkles, X, Plus, Loader2, AlertTriangle, Layers, Database,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Label } from '@/components/ui/label';
import {
    DropdownMenu, DropdownMenuContent, DropdownMenuCheckboxItem, DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { cn } from '@/lib/utils';
import { capabilityColors, getCapabilityColors } from '@/lib/capability-colors';
import { api } from '@/lib/api-client';
import { CapabilityPickerDrawer, type CapabilityPickerItem } from './CapabilityPickerDrawer';
import type { Agent } from '@/types/admin';
import type { AvailableResources } from '@/types/agent-graph';

const LOG_PREFIX = 'AgentConfigTab';

function matchesNameOrAlias<T extends { name: string; aliases?: string[] }>(
    resource: T,
    searchValue: string,
): boolean {
    return resource.name === searchValue || (resource.aliases?.includes(searchValue) ?? false);
}

function extractCapabilityName(toolRef: string): string {
    const idx = toolRef.indexOf('::');
    return idx >= 0 ? toolRef.slice(0, idx) : toolRef;
}

function extractMethodName(toolRef: string): string | null {
    const idx = toolRef.indexOf('::');
    return idx >= 0 ? toolRef.slice(idx + 2) : null;
}

function extractAppKey(capabilityRef?: string | null): string | null {
    if (!capabilityRef) return null;
    const capRef = capabilityRef.includes('::') ? extractCapabilityName(capabilityRef) : capabilityRef;
    if (!capRef.includes('/')) return null;
    return capRef.split('/', 2)[0] ?? null;
}

function groupToolsByCapability(toolRefs: string[]) {
    const map = new Map<string, { methods: string[]; refs: string[] }>();
    for (const ref of toolRefs) {
        const capability = extractCapabilityName(ref);
        const method = extractMethodName(ref);
        const entry = map.get(capability) ?? { methods: [], refs: [] };
        entry.refs.push(ref);
        if (method && !entry.methods.includes(method)) entry.methods.push(method);
        map.set(capability, entry);
    }
    return Array.from(map.entries()).map(([capability, { methods, refs }]) => ({
        capability, methods, refs,
    }));
}

/** Match {@link AgentNode} / graph: resolve capability by fqcn or short name. */
function findToolByCapabilityName(
    tools: Array<{ id?: number; name: string; aliases?: string[]; description?: string; type?: string; app_key?: string }>,
    capabilityName: string,
): { id?: number; name: string; description?: string; type?: string; app_key?: string } | null {
    let found = tools.find(t => matchesNameOrAlias(t, capabilityName)) ?? null;
    if (!found && capabilityName.includes('/')) {
        const shortName = capabilityName.split('/').pop() ?? capabilityName;
        found = tools.find(t => matchesNameOrAlias(t, shortName)) ?? null;
    }
    return found;
}

// ---------------------------------------------------------------------------
// Tool method dropdown (same as AgentNode)
// ---------------------------------------------------------------------------

interface ToolMethod {
    name: string;
    description?: string | null;
}

function ToolMethodDropdown(props: {
    capability: string;
    selectedMethods: string[];
    onToggle: (capability: string, method: string, checked: boolean) => void;
    appKey?: string;
}) {
    const { capability, selectedMethods, onToggle, appKey } = props;
    const [methods, setMethods] = useState<ToolMethod[]>([]);
    const [isLoading, setIsLoading] = useState(false);
    const [open, setOpen] = useState(false);

    const fetchMethods = useCallback(async () => {
        const parts = capability.includes('/') ? capability.split('/', 2) : null;
        const capAppKey = parts ? parts[0] : appKey;
        const capName = parts ? parts[1] : capability;
        if (!capAppKey || !capName) return;
        setIsLoading(true);
        try {
            const res = await api.get<{
                working_revision?: { methods?: ToolMethod[] };
                methods?: ToolMethod[];
            }>(`/applications/${encodeURIComponent(capAppKey)}/capabilities/${encodeURIComponent(capName)}`);
            setMethods(res.data?.working_revision?.methods ?? res.data?.methods ?? []);
        } catch (err) {
            console.error(`${LOG_PREFIX}.fetchMethods`, err);
        } finally {
            setIsLoading(false);
        }
    }, [capability, appKey]);

    const handleOpenChange = useCallback((next: boolean) => {
        setOpen(next);
        if (next && methods.length === 0 && !isLoading) fetchMethods();
    }, [methods.length, isLoading, fetchMethods]);

    return (
        <DropdownMenu open={open} onOpenChange={handleOpenChange}>
            <DropdownMenuTrigger asChild>
                <Button
                    variant="ghost"
                    size="icon"
                    className="h-6 w-6 shrink-0 rounded text-slate-400 hover:text-slate-600 hover:bg-slate-50"
                >
                    <Wrench size={12} />
                </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="min-w-[180px]">
                {isLoading ? (
                    <div className="flex items-center gap-2 px-2 py-3 text-sm text-slate-500">
                        <Loader2 size={14} className="animate-spin" /> Loading methods...
                    </div>
                ) : methods.length === 0 ? (
                    <div className="px-2 py-3 text-sm text-slate-500">No methods defined</div>
                ) : (
                    methods.map(m => (
                        <DropdownMenuCheckboxItem
                            key={m.name}
                            checked={selectedMethods.includes(m.name)}
                            onSelect={(e) => e.preventDefault()}
                            onCheckedChange={(checked) => onToggle(capability, m.name, !!checked)}
                        >
                            {m.name}
                        </DropdownMenuCheckboxItem>
                    ))
                )}
            </DropdownMenuContent>
        </DropdownMenu>
    );
}

// ---------------------------------------------------------------------------
// AgentConfigTab
// ---------------------------------------------------------------------------

export interface AgentConfigTabProps {
    agent: Agent;
    resources: AvailableResources;
    readOnly?: boolean;
    appKey?: string;
    onAgentChange: (agent: Agent) => void;
    onDirtyChange?: (dirty: boolean) => void;
    /** When provided, clicking an assigned model card fires this instead of opening the picker */
    onModelCardClick?: (modelRef: string) => void;
    /** When provided, clicking an assigned prompt card fires this instead of opening the picker */
    onPromptCardClick?: (promptRef: string) => void;
    /** When provided, clicking an assigned tool card fires this to open a tiled window */
    onToolCardClick?: (toolRef: string, capType?: string) => void;
    /** Current user ID (subject) for Private filter in picker */
    currentUserId?: string;
    /** Current organization ID for Team filter in picker */
    currentOrgId?: string;
}

type DrawerTarget = 'model' | 'prompt' | 'tool' | 'skill' | 'memory' | null;

export function AgentConfigTab({
    agent,
    resources,
    readOnly = false,
    appKey,
    onAgentChange,
    onDirtyChange,
    onModelCardClick,
    onPromptCardClick,
    onToolCardClick,
    currentUserId,
    currentOrgId,
}: AgentConfigTabProps) {
    const [currentAgent, setCurrentAgent] = useState(agent);
    const initialAgentRef = useRef(agent);
    const [openDrawer, setOpenDrawer] = useState<DrawerTarget>(null);

    useEffect(() => {
        setCurrentAgent(agent);
        initialAgentRef.current = agent;
    }, [agent]);

    const isDirty = useMemo(() => {
        const a = initialAgentRef.current;
        const b = currentAgent;
        const arrEq = (x: string[], y: string[]) => x.length === y.length && x.every((v, i) => v === y[i]);
        return a.model_ref !== b.model_ref || a.prompt_ref !== b.prompt_ref || !arrEq(a.tool_refs ?? [], b.tool_refs ?? []) || !arrEq(a.skill_refs ?? [], b.skill_refs ?? []) || !arrEq(a.memory_refs ?? [], b.memory_refs ?? []);
    }, [currentAgent]);

    useEffect(() => {
        onDirtyChange?.(isDirty);
    }, [isDirty, onDirtyChange]);

    const update = useCallback((updater: (prev: Agent) => Agent) => {
        setCurrentAgent(prev => {
            const next = updater(prev);
            onAgentChange(next);
            return next;
        });
    }, [onAgentChange]);

    // -- Model --
    const handleModelSelect = useCallback((name: string) => {
        update(prev => ({ ...prev, model_ref: name }));
    }, [update]);

    const handleModelRemove = useCallback(() => {
        update(prev => ({ ...prev, model_ref: null }));
    }, [update]);

    // -- Prompt --
    const handlePromptSelect = useCallback((name: string) => {
        update(prev => ({ ...prev, prompt_ref: name }));
    }, [update]);

    const handlePromptRemove = useCallback(() => {
        update(prev => ({ ...prev, prompt_ref: null }));
    }, [update]);

    // -- Tool --
    const handleToolSelect = useCallback(async (name: string) => {
        const parts = name.includes('/') ? name.split('/', 2) : null;
        const capAppKey = parts ? parts[0] : appKey;
        const capName = parts ? parts[1] : name;
        if (capAppKey && capName) {
            try {
                const res = await api.get<{
                    working_revision?: { methods?: ToolMethod[] };
                    methods?: ToolMethod[];
                }>(`/applications/${encodeURIComponent(capAppKey)}/capabilities/${encodeURIComponent(capName)}`);
                const methods = res.data?.working_revision?.methods ?? res.data?.methods ?? [];
                const toolFqcn = `${capAppKey}/${capName}`;
                if (methods.length > 0) {
                    const ref = `${toolFqcn}::${methods[0].name}`;
                    update(prev => {
                        if (prev.tool_refs.includes(ref)) return prev;
                        return { ...prev, tool_refs: [...prev.tool_refs, ref] };
                    });
                    return;
                }
            } catch {
                // fall through
            }
        }
        update(prev => {
            if (prev.tool_refs.includes(name)) return prev;
            return { ...prev, tool_refs: [...prev.tool_refs, name] };
        });
    }, [update, appKey]);

    const handleToolRemove = useCallback((capabilityKey: string) => {
        const toRemove = capabilityKey.includes('::')
            ? (t: string) => t === capabilityKey
            : (t: string) => t === capabilityKey || t.startsWith(capabilityKey + '::');
        update(prev => ({ ...prev, tool_refs: prev.tool_refs.filter(t => !toRemove(t)) }));
    }, [update]);

    const handleToolMethodToggle = useCallback((capability: string, method: string, checked: boolean) => {
        const ref = `${capability}::${method}`;
        update(prev => {
            if (checked) {
                if (prev.tool_refs.includes(ref)) return prev;
                return { ...prev, tool_refs: [...prev.tool_refs, ref] };
            }
            return { ...prev, tool_refs: prev.tool_refs.filter(t => t !== ref) };
        });
    }, [update]);

    // -- Skill --
    const handleSkillSelect = useCallback((name: string) => {
        update(prev => {
            const skill_refs = prev.skill_refs ?? [];
            if (skill_refs.includes(name)) return prev;
            return { ...prev, skill_refs: [...skill_refs, name] };
        });
    }, [update]);

    const handleSkillRemove = useCallback((name: string) => {
        update(prev => ({
            ...prev,
            skill_refs: prev.skill_refs ? prev.skill_refs.filter(s => s !== name) : [],
        }));
    }, [update]);

    // -- Memory --
    const handleMemorySelect = useCallback((name: string) => {
        update(prev => {
            const memory_refs = prev.memory_refs ?? [];
            if (memory_refs.includes(name)) return prev;
            return { ...prev, memory_refs: [...memory_refs, name] };
        });
    }, [update]);

    const handleMemoryRemove = useCallback((name: string) => {
        update(prev => ({
            ...prev,
            memory_refs: prev.memory_refs ? prev.memory_refs.filter(m => m !== name) : [],
        }));
    }, [update]);

    // -- Drawer select dispatcher --
    const handleDrawerSelect = useCallback((name: string) => {
        switch (openDrawer) {
            case 'model': handleModelSelect(name); break;
            case 'prompt': handlePromptSelect(name); break;
            case 'tool': handleToolSelect(name); break;
            case 'skill': handleSkillSelect(name); break;
            case 'memory': handleMemorySelect(name); break;
        }
    }, [openDrawer, handleModelSelect, handlePromptSelect, handleToolSelect, handleSkillSelect, handleMemorySelect]);

    // -- Drawer items for current target --
    const drawerItems = useMemo<CapabilityPickerItem[]>(() => {
        switch (openDrawer) {
            case 'model': return resources.models as CapabilityPickerItem[];
            case 'prompt': return resources.prompts as CapabilityPickerItem[];
            case 'tool': return resources.tools as CapabilityPickerItem[];
            case 'skill': return resources.skills as CapabilityPickerItem[];
            case 'memory': return resources.memories as CapabilityPickerItem[];
            default: return [];
        }
    }, [openDrawer, resources]);

    const drawerSelectedRefs = useMemo<string[]>(() => {
        switch (openDrawer) {
            case 'model': return currentAgent.model_ref ? [currentAgent.model_ref] : [];
            case 'prompt': return currentAgent.prompt_ref ? [currentAgent.prompt_ref] : [];
            case 'tool': return currentAgent.tool_refs ?? [];
            case 'skill': return currentAgent.skill_refs ?? [];
            case 'memory': return currentAgent.memory_refs ?? [];
            default: return [];
        }
    }, [openDrawer, currentAgent]);

    // -- Resolve display info --
    const modelInfo = currentAgent.model_ref
        ? resources.models.find(m => matchesNameOrAlias(m, currentAgent.model_ref!))
        : undefined;
    const promptInfo = currentAgent.prompt_ref
        ? resources.prompts.find(p => matchesNameOrAlias(p, currentAgent.prompt_ref!))
        : undefined;
    const modelAppKey = modelInfo?.app_key ?? extractAppKey(currentAgent.model_ref);
    const promptAppKey = promptInfo?.app_key ?? extractAppKey(currentAgent.prompt_ref);

    const toolDisplayItems = groupToolsByCapability(currentAgent.tool_refs ?? []).map(({ capability, methods }) => {
        const found = findToolByCapabilityName(resources.tools, capability);
        return { key: capability, name: capability, methods, found, isResolved: !!found, capType: found?.type ?? 'tool' };
    });

    const skillDisplayItems = (currentAgent.skill_refs ?? []).map(name => {
        const found = resources.skills.find(s => matchesNameOrAlias(s, name));
        return { key: name, name, found };
    });

    const memoryDisplayItems = (currentAgent.memory_refs ?? []).map(name => {
        const found = resources.memories.find(m => matchesNameOrAlias(m, name));
        return { key: name, name, found };
    });

    const openSlot = (target: DrawerTarget) => {
        if (!readOnly) setOpenDrawer(target);
    };

    return (
        <div className="flex flex-col gap-6">
            <h2 className="text-sm font-semibold text-slate-900">Agent configuration</h2>

            <div className="grid grid-cols-1 gap-6 sm:grid-cols-4">
                {/* ============ Column 1: Model + Prompt ============ */}
                <div className="flex flex-col gap-6">
                    {/* --- Model --- */}
                    <div className="space-y-2">
                        <Label>Model</Label>
                        <div
                            role="button"
                            tabIndex={currentAgent.model_ref && onModelCardClick ? 0 : (readOnly ? -1 : 0)}
                            onClick={() => {
                                if (currentAgent.model_ref && onModelCardClick) {
                                    onModelCardClick(currentAgent.model_ref);
                                } else if (!readOnly) {
                                    openSlot('model');
                                }
                            }}
                            onKeyDown={e => {
                                if (e.key === 'Enter' || e.key === ' ') {
                                    e.preventDefault();
                                    if (currentAgent.model_ref && onModelCardClick) {
                                        onModelCardClick(currentAgent.model_ref);
                                    } else if (!readOnly) {
                                        openSlot('model');
                                    }
                                }
                            }}
                            className={cn((currentAgent.model_ref && onModelCardClick) ? 'cursor-pointer' : (!readOnly && 'cursor-pointer'))}
                        >
                            {currentAgent.model_ref ? (
                                <Card
                                    className={cn(
                                        'relative border-2 p-3 transition-all duration-200 min-h-[88px]',
                                        'border-l-4 border-slate-200 bg-white hover:shadow-md',
                                    )}
                                    style={{ borderLeftColor: capabilityColors.model.hex }}
                                >
                                    <div className="flex flex-row items-start gap-3">
                                        <div className={cn(
                                            'flex h-8 w-8 shrink-0 items-center justify-center rounded-md',
                                            capabilityColors.model.bgLight, capabilityColors.model.text,
                                        )}>
                                            <Brain size={16} />
                                        </div>
                                        <div className="flex flex-col flex-1 min-w-0 gap-0.5">
                                            <span className="text-[10px] font-medium uppercase tracking-wide text-slate-500">
                                                model
                                            </span>
                                            <span className="text-sm font-bold truncate text-slate-900">
                                                {modelInfo?.name ?? currentAgent.model_ref}
                                            </span>
                                            {modelAppKey && (
                                                <span className="font-mono text-[10px] text-brand">
                                                    {modelAppKey}
                                                </span>
                                            )}
                                            {modelInfo?.provider && (
                                                <span className={cn('text-[10px] truncate', capabilityColors.model.text)}>
                                                    {modelInfo.provider}
                                                </span>
                                            )}
                                            {modelInfo?.description && (
                                                <span className="text-[10px] text-slate-500 line-clamp-2 leading-tight">
                                                    {modelInfo.description}
                                                </span>
                                            )}
                                        </div>
                                        {!readOnly && (
                                            <Button
                                                type="button"
                                                variant="ghost"
                                                size="icon"
                                                className="h-5 w-5 shrink-0 rounded-full text-slate-400 hover:text-red-500 hover:bg-red-50"
                                                onClick={e => { e.stopPropagation(); handleModelRemove(); }}
                                                aria-label="Remove model"
                                            >
                                                <X size={10} />
                                            </Button>
                                        )}
                                    </div>
                                </Card>
                            ) : (
                                <Card className={cn(
                                    'border-2 border-dashed border-slate-200 bg-slate-50/95 p-3',
                                    readOnly && 'pointer-events-none opacity-60',
                                )}>
                                    <div className="flex flex-col items-center justify-center py-2">
                                        <span className={cn('mb-1', capabilityColors.model.text)}><Brain size={24} /></span>
                                        <p className={cn('text-xs font-medium', capabilityColors.model.text)}>
                                            {readOnly ? 'No model' : 'Click to select model'}
                                        </p>
                                    </div>
                                </Card>
                            )}
                        </div>
                    </div>

                    {/* --- Prompt --- */}
                    <div className="space-y-2">
                        <Label>Prompt</Label>
                        <div
                            role="button"
                            tabIndex={currentAgent.prompt_ref && onPromptCardClick ? 0 : (readOnly ? -1 : 0)}
                            onClick={() => {
                                if (currentAgent.prompt_ref && onPromptCardClick) {
                                    onPromptCardClick(currentAgent.prompt_ref);
                                } else if (!readOnly) {
                                    openSlot('prompt');
                                }
                            }}
                            onKeyDown={e => {
                                if (e.key === 'Enter' || e.key === ' ') {
                                    e.preventDefault();
                                    if (currentAgent.prompt_ref && onPromptCardClick) {
                                        onPromptCardClick(currentAgent.prompt_ref);
                                    } else if (!readOnly) {
                                        openSlot('prompt');
                                    }
                                }
                            }}
                            className={cn((currentAgent.prompt_ref && onPromptCardClick) ? 'cursor-pointer' : (!readOnly && 'cursor-pointer'))}
                        >
                            {currentAgent.prompt_ref ? (
                                <Card
                                    className={cn(
                                        'relative border-2 p-3 transition-all duration-200 min-h-[88px]',
                                        'border-l-4 border-slate-200 bg-white hover:shadow-md',
                                    )}
                                    style={{ borderLeftColor: capabilityColors.prompt.hex }}
                                >
                                    <div className="flex flex-row items-start gap-3">
                                        <div className={cn(
                                            'flex h-8 w-8 shrink-0 items-center justify-center rounded-md',
                                            capabilityColors.prompt.bgLight, capabilityColors.prompt.text,
                                        )}>
                                            <FileText size={16} />
                                        </div>
                                        <div className="flex flex-col flex-1 min-w-0 gap-0.5">
                                            <span className="text-[10px] font-medium uppercase tracking-wide text-slate-500">
                                                prompt
                                            </span>
                                            <span className="text-sm font-bold truncate text-slate-900">
                                                {promptInfo?.name ?? currentAgent.prompt_ref}
                                            </span>
                                            {promptAppKey && (
                                                <span className="font-mono text-[10px] text-brand">
                                                    {promptAppKey}
                                                </span>
                                            )}
                                            {promptInfo?.description && (
                                                <span className="text-[10px] text-slate-500 line-clamp-2 leading-tight">
                                                    {promptInfo.description}
                                                </span>
                                            )}
                                        </div>
                                        {!readOnly && (
                                            <Button
                                                type="button"
                                                variant="ghost"
                                                size="icon"
                                                className="h-5 w-5 shrink-0 rounded-full text-slate-400 hover:text-red-500 hover:bg-red-50"
                                                onClick={e => { e.stopPropagation(); handlePromptRemove(); }}
                                                aria-label="Remove prompt"
                                            >
                                                <X size={10} />
                                            </Button>
                                        )}
                                    </div>
                                </Card>
                            ) : (
                                <Card className={cn(
                                    'border-2 border-dashed border-slate-200 bg-slate-50/95 p-3',
                                    readOnly && 'pointer-events-none opacity-60',
                                )}>
                                    <div className="flex flex-col items-center justify-center py-2">
                                        <span className={cn('mb-1', capabilityColors.prompt.text)}><FileText size={24} /></span>
                                        <p className={cn('text-xs font-medium', capabilityColors.prompt.text)}>
                                            {readOnly ? 'No prompt' : 'Click to select prompt'}
                                        </p>
                                    </div>
                                </Card>
                            )}
                        </div>
                    </div>
                </div>

                {/* ============ Column 2: Tools ============ */}
                <div className="space-y-2">
                    <div className="flex items-center gap-2">
                        <Label>Capabilities</Label>
                        {toolDisplayItems.length > 0 && (
                            <span className="text-[10px] px-1.5 py-0.5 rounded font-medium bg-slate-100 text-slate-700">
                                {toolDisplayItems.length}
                            </span>
                        )}
                    </div>
                    <div className="flex flex-col gap-2">
                        {toolDisplayItems.map(({ key, name, methods, found, isResolved, capType }) => {
                            const displayName = found?.name
                                ?? (name.includes('/') && name.split('/')[0] === appKey ? name.split('/').pop() : name);
                            const displayAppKey = found?.app_key ?? extractAppKey(key);
                            const canClickToOpen = isResolved && !!onToolCardClick;
                            const itemColors = isResolved ? getCapabilityColors(capType) : capabilityColors.tool;
                            const ItemIcon = itemColors.icon;
                            return (
                                <Card
                                    key={key}
                                    role={canClickToOpen ? 'button' : undefined}
                                    tabIndex={canClickToOpen ? 0 : undefined}
                                    onClick={canClickToOpen ? () => onToolCardClick(key, capType) : undefined}
                                    onKeyDown={canClickToOpen ? (e) => {
                                        if (e.key === 'Enter' || e.key === ' ') {
                                            e.preventDefault();
                                            onToolCardClick(key, capType);
                                        }
                                    } : undefined}
                                    className={cn(
                                        'relative border-2 p-3 transition-all duration-200',
                                        'border-l-4',
                                        isResolved
                                            ? 'border-slate-200 bg-white hover:shadow-md'
                                            : 'border-slate-200 bg-red-50',
                                        canClickToOpen && 'cursor-pointer',
                                    )}
                                    style={{
                                        borderLeftColor: isResolved ? itemColors.hex : '#ef4444',
                                    }}
                                >
                                    <div className="flex flex-col gap-2">
                                        <div className="flex flex-row items-start gap-3">
                                            <div className={cn(
                                                'flex h-8 w-8 shrink-0 items-center justify-center rounded-md',
                                                isResolved
                                                    ? `${itemColors.bgLight} ${itemColors.text}`
                                                    : 'bg-red-100 text-red-600',
                                            )}>
                                                {isResolved ? <ItemIcon size={16} /> : <AlertTriangle size={16} />}
                                            </div>
                                            <div className="flex flex-col flex-1 min-w-0 gap-0.5">
                                                <span className={cn(
                                                    'text-[10px] font-medium uppercase tracking-wide',
                                                    isResolved ? itemColors.text : 'text-slate-500',
                                                )}>
                                                    {capType}
                                                </span>
                                                <span className={cn(
                                                    'text-sm font-bold truncate',
                                                    isResolved ? 'text-slate-900' : 'text-red-600',
                                                )}>
                                                    {displayName}
                                                </span>
                                                {displayAppKey && (
                                                    <span className="font-mono text-[10px] text-brand">
                                                        {displayAppKey}
                                                    </span>
                                                )}
                                                {found?.description && (
                                                    <span className="text-[10px] text-slate-500 line-clamp-2 leading-tight">
                                                        {found.description}
                                                    </span>
                                                )}
                                                {!isResolved && (
                                                    <span className="text-[10px] text-red-500">
                                                        Not found in registry
                                                    </span>
                                                )}
                                            </div>
                                            {!readOnly && (
                                                <Button
                                                    type="button"
                                                    variant="ghost"
                                                    size="icon"
                                                    className="h-5 w-5 shrink-0 rounded-full text-slate-400 hover:text-red-500 hover:bg-red-50"
                                                    onClick={e => { e.stopPropagation(); handleToolRemove(key); }}
                                                    aria-label="Remove capability"
                                                >
                                                    <X size={10} />
                                                </Button>
                                            )}
                                        </div>
                                        {/* eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions */}
                                        <div
                                            className="flex flex-row items-center justify-between gap-2 min-h-[24px]"
                                            onClick={(e) => e.stopPropagation()}
                                            onPointerDown={(e) => e.stopPropagation()}
                                        >
                                            <div className="flex flex-wrap gap-1 flex-1 min-w-0">
                                                {methods.map(m => (
                                                    <Badge
                                                        key={m}
                                                        variant="secondary"
                                                        className={cn('text-[10px] px-1.5 py-0 font-medium', itemColors.bgLight, itemColors.text)}
                                                    >
                                                        {m}
                                                    </Badge>
                                                ))}
                                            </div>
                                            {!readOnly && (
                                                <ToolMethodDropdown
                                                    capability={key}
                                                    selectedMethods={methods}
                                                    onToggle={handleToolMethodToggle}
                                                    appKey={appKey}
                                                />
                                            )}
                                        </div>
                                    </div>
                                </Card>
                            );
                        })}

                        {/* Empty / Add slot */}
                        <div
                            role="button"
                            tabIndex={readOnly ? -1 : 0}
                            onClick={() => openSlot('tool')}
                            onKeyDown={e => {
                                if (readOnly) return;
                                if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); openSlot('tool'); }
                            }}
                            className={cn(!readOnly && 'cursor-pointer')}
                        >
                            <Card className={cn(
                                'border-2 border-dashed border-slate-200 bg-slate-50/95 p-3',
                                readOnly && 'pointer-events-none opacity-60',
                            )}>
                                <div className="flex flex-col items-center justify-center py-2">
                                    {toolDisplayItems.length === 0 ? (
                                        <>
                                            <span className="mb-1 text-slate-500"><Layers size={24} /></span>
                                            <p className="text-xs font-medium text-slate-500">
                                                {readOnly ? 'No capabilities' : 'Click to add capability'}
                                            </p>
                                        </>
                                    ) : (
                                        <div className="flex items-center gap-1.5">
                                            <Plus size={14} className="text-slate-500" />
                                            <span className="text-xs font-medium text-slate-500">Add capability</span>
                                        </div>
                                    )}
                                </div>
                            </Card>
                        </div>
                    </div>
                </div>

                {/* ============ Column 3: Skills ============ */}
                <div className="space-y-2">
                    <div className="flex items-center gap-2">
                        <Label>Skills</Label>
                        {skillDisplayItems.length > 0 && (
                            <span className="text-[10px] px-1.5 py-0.5 rounded font-medium bg-fuchsia-100 text-fuchsia-700">
                                {skillDisplayItems.length}
                            </span>
                        )}
                    </div>
                    <div className="flex flex-col gap-2">
                        {skillDisplayItems.map(({ key, name, found }) => {
                            const displayName = found?.name ?? name;
                            const displayAppKey = found?.app_key ?? extractAppKey(name);
                            return (
                                <Card
                                    key={key}
                                    className={cn(
                                        'relative border-2 p-3 transition-all duration-200 min-h-[88px]',
                                        'border-l-4 border-slate-200 bg-white hover:shadow-md',
                                    )}
                                    style={{ borderLeftColor: capabilityColors.skill.hex }}
                                >
                                    <div className="flex flex-row items-start gap-3">
                                        <div className={cn(
                                            'flex h-8 w-8 shrink-0 items-center justify-center rounded-md',
                                            capabilityColors.skill.bgLight, capabilityColors.skill.text,
                                        )}>
                                            <Sparkles size={16} />
                                        </div>
                                        <div className="flex flex-col flex-1 min-w-0 gap-0.5">
                                            <span className="text-[10px] font-medium uppercase tracking-wide text-slate-500">
                                                skill
                                            </span>
                                            <span className="text-sm font-bold truncate text-slate-900">
                                                {displayName}
                                            </span>
                                            {displayAppKey && (
                                                <span className="font-mono text-[10px] text-brand">
                                                    {displayAppKey}
                                                </span>
                                            )}
                                            {found?.description && (
                                                <span className="text-[10px] text-slate-500 line-clamp-2 leading-tight">
                                                    {found.description}
                                                </span>
                                            )}
                                        </div>
                                        {!readOnly && (
                                            <Button
                                                type="button"
                                                variant="ghost"
                                                size="icon"
                                                className="h-5 w-5 shrink-0 rounded-full text-slate-400 hover:text-red-500 hover:bg-red-50"
                                                onClick={e => { e.stopPropagation(); handleSkillRemove(key); }}
                                                aria-label="Remove skill"
                                            >
                                                <X size={10} />
                                            </Button>
                                        )}
                                    </div>
                                </Card>
                            );
                        })}

                        {/* Empty / Add slot */}
                        <div
                            role="button"
                            tabIndex={readOnly ? -1 : 0}
                            onClick={() => openSlot('skill')}
                            onKeyDown={e => {
                                if (readOnly) return;
                                if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); openSlot('skill'); }
                            }}
                            className={cn(!readOnly && 'cursor-pointer')}
                        >
                            <Card className={cn(
                                'border-2 border-dashed border-slate-200 bg-slate-50/95 p-3',
                                readOnly && 'pointer-events-none opacity-60',
                            )}>
                                <div className="flex flex-col items-center justify-center py-2">
                                    {skillDisplayItems.length === 0 ? (
                                        <>
                                            <span className={cn('mb-1', capabilityColors.skill.text)}><Sparkles size={24} /></span>
                                            <p className={cn('text-xs font-medium', capabilityColors.skill.text)}>
                                                {readOnly ? 'No skills' : 'Click to add skill'}
                                            </p>
                                        </>
                                    ) : (
                                        <div className="flex items-center gap-1.5">
                                            <Plus size={14} className={capabilityColors.skill.text} />
                                            <span className={cn('text-xs font-medium', capabilityColors.skill.text)}>Add skill</span>
                                        </div>
                                    )}
                                </div>
                            </Card>
                        </div>
                    </div>
                </div>

                {/* ============ Column 4: Memory ============ */}
                <div className="space-y-2">
                    <div className="flex items-center gap-2">
                        <Label>Memory</Label>
                        {memoryDisplayItems.length > 0 && (
                            <span className="text-[10px] px-1.5 py-0.5 rounded font-medium bg-cyan-100 text-cyan-700">
                                {memoryDisplayItems.length}
                            </span>
                        )}
                    </div>
                    <div className="flex flex-col gap-2">
                        {memoryDisplayItems.map(({ key, name, found }) => {
                            const displayName = found?.name ?? name;
                            const displayAppKey = found?.app_key ?? extractAppKey(name);
                            return (
                                <Card
                                    key={key}
                                    className={cn(
                                        'relative border-2 p-3 transition-all duration-200 min-h-[88px]',
                                        'border-l-4 border-slate-200 bg-white hover:shadow-md',
                                    )}
                                    style={{ borderLeftColor: capabilityColors.memory.hex }}
                                >
                                    <div className="flex flex-row items-start gap-3">
                                        <div className={cn(
                                            'flex h-8 w-8 shrink-0 items-center justify-center rounded-md',
                                            capabilityColors.memory.bgLight, capabilityColors.memory.text,
                                        )}>
                                            <Database size={16} />
                                        </div>
                                        <div className="flex flex-col flex-1 min-w-0 gap-0.5">
                                            <span className="text-[10px] font-medium uppercase tracking-wide text-slate-500">
                                                memory
                                            </span>
                                            <span className="text-sm font-bold truncate text-slate-900">
                                                {displayName}
                                            </span>
                                            {displayAppKey && (
                                                <span className="font-mono text-[10px] text-brand">
                                                    {displayAppKey}
                                                </span>
                                            )}
                                            {found?.description && (
                                                <span className="text-[10px] text-slate-500 line-clamp-2 leading-tight">
                                                    {found.description}
                                                </span>
                                            )}
                                        </div>
                                        {!readOnly && (
                                            <Button
                                                type="button"
                                                variant="ghost"
                                                size="icon"
                                                className="h-5 w-5 shrink-0 rounded-full text-slate-400 hover:text-red-500 hover:bg-red-50"
                                                onClick={e => { e.stopPropagation(); handleMemoryRemove(key); }}
                                                aria-label="Remove memory"
                                            >
                                                <X size={10} />
                                            </Button>
                                        )}
                                    </div>
                                </Card>
                            );
                        })}

                        {/* Empty / Add slot */}
                        <div
                            role="button"
                            tabIndex={readOnly ? -1 : 0}
                            onClick={() => openSlot('memory')}
                            onKeyDown={e => {
                                if (readOnly) return;
                                if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); openSlot('memory'); }
                            }}
                            className={cn(!readOnly && 'cursor-pointer')}
                        >
                            <Card className={cn(
                                'border-2 border-dashed border-slate-200 bg-slate-50/95 p-3',
                                readOnly && 'pointer-events-none opacity-60',
                            )}>
                                <div className="flex flex-col items-center justify-center py-2">
                                    {memoryDisplayItems.length === 0 ? (
                                        <>
                                            <span className={cn('mb-1', capabilityColors.memory.text)}><Database size={24} /></span>
                                            <p className={cn('text-xs font-medium', capabilityColors.memory.text)}>
                                                {readOnly ? 'No memory' : 'Click to add memory'}
                                            </p>
                                        </>
                                    ) : (
                                        <div className="flex items-center gap-1.5">
                                            <Plus size={14} className={capabilityColors.memory.text} />
                                            <span className={cn('text-xs font-medium', capabilityColors.memory.text)}>Add memory</span>
                                        </div>
                                    )}
                                </div>
                            </Card>
                        </div>
                    </div>
                </div>
            </div>

            {/* Shared picker drawer */}
            {openDrawer && (
                <CapabilityPickerDrawer
                    isOpen={!!openDrawer}
                    onClose={() => setOpenDrawer(null)}
                    slotType={openDrawer}
                    items={drawerItems}
                    selectedRefs={drawerSelectedRefs}
                    onSelect={handleDrawerSelect}
                    appKey={appKey}
                    currentUserId={currentUserId}
                    currentOrgId={currentOrgId}
                />
            )}
        </div>
    );
}
